import { useEffect, useRef, useState } from "react";
import { api } from "../lib/api";
import Layout from "../components/Layout";
import RichText from "../components/RichText";
import { CoverSplashContent, COVER_ACCENTS } from "../components/CoverSplash";
import {
  CircleNotch, EyeSlash, Hourglass, Wrench, Sparkle, UploadSimple,
  ArrowCounterClockwise, FloppyDisk, CheckCircle,
} from "@phosphor-icons/react";

const MODE_TABS = [
  { id: "coming_soon", label: "Coming soon", icon: Hourglass },
  { id: "maintenance", label: "Maintenance", icon: Wrench },
  { id: "custom", label: "Custom", icon: Sparkle },
];

const ACCENT_KEYS = Object.keys(COVER_ACCENTS);

const DEFAULTS = {
  coming_soon: {
    eyebrow: "Asset Hub", title: "Something useful is on the way.",
    body: "<p>We're putting the finishing touches on Asset Hub for GBE Group.</p><p>Please check back shortly — we'll be live soon.</p>",
    button_label: "Contact us", button_url: "mailto:support@gbegroup.com.au", hero_image: "", accent: "emerald",
  },
  maintenance: {
    eyebrow: "Scheduled maintenance", title: "We'll be back shortly.",
    body: "<p>Asset Hub is undergoing scheduled maintenance and is temporarily unavailable.</p><p>Thanks for your patience — we'll have everything back up soon.</p>",
    button_label: "", button_url: "", hero_image: "", accent: "amber",
  },
  custom: {
    eyebrow: "", title: "Notice", body: "<p>Add your own message here.</p>",
    button_label: "", button_url: "", hero_image: "", accent: "slate",
  },
};

function Field({ label, children }) {
  return (
    <div>
      <label className="mb-1.5 block text-sm font-semibold text-neutral-700">{label}</label>
      {children}
    </div>
  );
}

export default function CoverPages() {
  const [config, setConfig] = useState(null);
  const [activeMode, setActiveMode] = useState("coming_soon");
  const [saving, setSaving] = useState(false);
  const [savedFlash, setSavedFlash] = useState(false);
  const fileRef = useRef(null);

  useEffect(() => {
    api.get("/cover").then((r) => {
      setConfig(r.data);
      setActiveMode(r.data.mode || "coming_soon");
    }).catch(() => {});
  }, []);

  const content = config?.modes?.[activeMode] || {};

  const setField = (field, value) => {
    setConfig((p) => ({ ...p, modes: { ...p.modes, [activeMode]: { ...p.modes[activeMode], [field]: value } } }));
  };

  const flash = () => { setSavedFlash(true); setTimeout(() => setSavedFlash(false), 2200); };

  const persist = async (patch) => {
    setSaving(true);
    try {
      const r = await api.put("/cover", patch);
      setConfig(r.data);
      flash();
    } finally {
      setSaving(false);
    }
  };

  const toggleEnabled = async (val) => {
    setConfig((p) => ({ ...p, enabled: val })); // optimistic
    await persist({ enabled: val });
  };

  const selectMode = (m) => {
    setActiveMode(m);
    setConfig((p) => ({ ...p, mode: m })); // active mode shown to visitors
  };

  const saveChanges = () => persist({ enabled: config.enabled, mode: config.mode, modes: config.modes });

  const resetMode = () => setConfig((p) => ({
    ...p, modes: { ...p.modes, [activeMode]: { ...DEFAULTS[activeMode] } },
  }));

  const onUpload = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.type.startsWith("image/")) { alert("Please choose an image file."); return; }
    if (file.size > 5 * 1024 * 1024) { alert("Image must be under 5MB."); return; }
    const reader = new FileReader();
    reader.onload = () => setField("hero_image", reader.result);
    reader.readAsDataURL(file);
  };

  if (!config) {
    return <Layout><div className="flex h-60 items-center justify-center"><CircleNotch size={28} className="animate-spin text-[#0055FF]" /></div></Layout>;
  }

  return (
    <Layout>
      <div data-testid="cover-pages-page">
        <p className="text-sm uppercase tracking-widest font-semibold text-neutral-500">Site control</p>
        <h1 className="mt-1 font-heading text-4xl sm:text-5xl font-black tracking-tighter text-[#0A0A0A]">Cover Pages</h1>
        <p className="mt-2 max-w-3xl text-base text-neutral-600">
          Customise the splash screen visitors see for <span className="font-semibold">Coming soon</span>, <span className="font-semibold">Maintenance</span> and <span className="font-semibold">Custom</span> modes, then flip the cover on to take the site offline for everyone except admins.
        </p>

        {/* Master toggle */}
        <div className="mt-6 flex items-center justify-between rounded-sm border border-neutral-200 bg-white p-4 shadow-sm" data-testid="cover-master-toggle-card">
          <div className="flex items-start gap-3">
            <div className={`flex h-9 w-9 items-center justify-center rounded-sm ${config.enabled ? "bg-[#D97706]/10 text-[#D97706]" : "bg-neutral-100 text-neutral-400"}`}>
              <EyeSlash size={18} weight="bold" />
            </div>
            <div>
              <div className="flex items-center gap-2">
                <p className="font-semibold text-[#0A0A0A]">Cover page</p>
                <span className={`rounded-sm px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider ${config.enabled ? "bg-[#D97706]/10 text-[#D97706]" : "bg-neutral-100 text-neutral-500"}`}>
                  {config.enabled ? `On · ${MODE_TABS.find((m) => m.id === config.mode)?.label}` : "Off · site is live"}
                </span>
              </div>
              <p className="mt-0.5 text-sm text-neutral-500">Flip this on to show the currently selected mode below to public visitors.</p>
            </div>
          </div>
          <button
            role="switch"
            aria-checked={config.enabled}
            data-testid="cover-enabled-toggle"
            onClick={() => toggleEnabled(!config.enabled)}
            disabled={saving}
            className={`relative inline-flex h-7 w-14 shrink-0 items-center rounded-full transition-colors duration-200 ${config.enabled ? "bg-[#D97706]" : "bg-neutral-300"}`}
          >
            <span className={`inline-block h-5 w-5 transform rounded-full bg-white shadow transition-transform duration-200 ${config.enabled ? "translate-x-8" : "translate-x-1"}`} />
          </button>
        </div>

        {/* Mode tabs */}
        <div className="mt-6 inline-flex gap-1 rounded-sm border border-neutral-200 bg-white p-1 shadow-sm">
          {MODE_TABS.map((t) => (
            <button key={t.id} data-testid={`cover-mode-${t.id}`} onClick={() => selectMode(t.id)}
              className={`flex items-center gap-2 rounded-sm px-4 py-2 text-sm font-semibold transition-all duration-200 ${activeMode === t.id ? "bg-[#0A0A0A] text-white" : "text-neutral-600 hover:bg-neutral-100"}`}>
              <t.icon size={16} weight="bold" />{t.label}
            </button>
          ))}
        </div>
        <p className="mt-3 text-sm text-neutral-500">
          Shown to public visitors when the cover is on and the active mode is <span className="font-mono">{activeMode}</span>. Authenticated admins always bypass.
        </p>

        <div className="mt-5 grid gap-6 lg:grid-cols-2">
          {/* Editor */}
          <div className="space-y-5 rounded-sm border border-neutral-200 bg-white p-6 shadow-sm">
            <Field label="Eyebrow (small label above the title)">
              <input data-testid="cover-field-eyebrow" value={content.eyebrow || ""} onChange={(e) => setField("eyebrow", e.target.value)}
                className="w-full rounded-sm border border-neutral-300 bg-white px-3 py-2.5 text-base outline-none focus:border-[#0055FF]" />
            </Field>
            <Field label="Title">
              <input data-testid="cover-field-title" value={content.title || ""} onChange={(e) => setField("title", e.target.value)}
                className="w-full rounded-sm border border-neutral-300 bg-white px-3 py-2.5 text-base outline-none focus:border-[#0055FF]" />
            </Field>
            <Field label="Body">
              <RichText value={content.body || ""} onChange={(v) => setField("body", v)} testid="cover-field-body" />
            </Field>
            <div className="grid grid-cols-2 gap-4">
              <Field label="Button label">
                <input data-testid="cover-field-button-label" value={content.button_label || ""} onChange={(e) => setField("button_label", e.target.value)}
                  className="w-full rounded-sm border border-neutral-300 bg-white px-3 py-2.5 text-base outline-none focus:border-[#0055FF]" />
              </Field>
              <Field label="Button URL">
                <input data-testid="cover-field-button-url" value={content.button_url || ""} onChange={(e) => setField("button_url", e.target.value)}
                  className="w-full rounded-sm border border-neutral-300 bg-white px-3 py-2.5 text-base outline-none focus:border-[#0055FF]" />
              </Field>
            </div>
            <Field label="Hero image (optional)">
              <div className="flex items-center gap-4">
                {content.hero_image ? (
                  <img src={content.hero_image} alt="" className="h-16 w-16 rounded-sm border border-neutral-200 object-contain" data-testid="cover-hero-thumb" />
                ) : (
                  <div className="flex h-16 w-16 items-center justify-center rounded-sm border border-dashed border-neutral-300 text-neutral-300"><UploadSimple size={20} /></div>
                )}
                <div className="flex items-center gap-3">
                  <button type="button" data-testid="cover-hero-upload" onClick={() => fileRef.current?.click()}
                    className="inline-flex items-center gap-2 rounded-sm border border-neutral-300 px-3 py-2 text-sm font-semibold text-neutral-700 hover:bg-neutral-50">
                    <UploadSimple size={15} weight="bold" />{content.hero_image ? "Replace image" : "Upload image"}
                  </button>
                  {content.hero_image ? (
                    <button type="button" data-testid="cover-hero-remove" onClick={() => setField("hero_image", "")} className="text-sm font-medium text-neutral-500 hover:text-[#DC2626]">Remove</button>
                  ) : null}
                  <input ref={fileRef} type="file" accept="image/*" className="hidden" onChange={onUpload} />
                </div>
              </div>
              <p className="mt-1.5 text-xs text-neutral-400">PNG, JPG, WebP, GIF or SVG · max 5MB</p>
            </Field>
            <Field label="Accent tone">
              <div className="flex flex-wrap gap-2">
                {ACCENT_KEYS.map((k) => (
                  <button key={k} type="button" data-testid={`cover-accent-${k}`} onClick={() => setField("accent", k)}
                    className={`flex items-center gap-2 rounded-sm border px-3 py-1.5 text-sm font-semibold capitalize transition-all duration-150 ${content.accent === k ? "border-[#0A0A0A] bg-[#0A0A0A] text-white" : "border-neutral-300 bg-white text-neutral-600 hover:bg-neutral-50"}`}>
                    <span className={`h-3 w-3 rounded-full ${COVER_ACCENTS[k].dot}`} />{k}
                  </button>
                ))}
              </div>
            </Field>

            <div className="flex items-center justify-between border-t border-neutral-200 pt-4">
              <p className="text-xs text-neutral-400">
                {config.updated_at ? `Last saved ${new Date(config.updated_at).toLocaleString()} by ${config.updated_by || "admin"}` : "Not saved yet"}
              </p>
              <div className="flex items-center gap-2">
                <button data-testid="cover-reset-btn" onClick={resetMode}
                  className="inline-flex items-center gap-2 rounded-sm border border-neutral-300 px-3 py-2 text-sm font-semibold text-neutral-700 hover:bg-neutral-50">
                  <ArrowCounterClockwise size={15} weight="bold" />Reset to default
                </button>
                <button data-testid="cover-save-btn" onClick={saveChanges} disabled={saving}
                  className="inline-flex items-center gap-2 rounded-sm bg-[#059669] px-4 py-2 text-sm font-semibold text-white hover:bg-[#047857] disabled:opacity-60">
                  {saving ? <CircleNotch size={15} className="animate-spin" /> : savedFlash ? <CheckCircle size={15} weight="fill" /> : <FloppyDisk size={15} weight="bold" />}
                  {savedFlash ? "Saved" : "Save changes"}
                </button>
              </div>
            </div>
          </div>

          {/* Live preview */}
          <div className="rounded-sm border border-neutral-200 bg-white shadow-sm">
            <div className="flex items-center justify-between border-b border-neutral-200 px-5 py-3">
              <span className="text-xs font-bold uppercase tracking-widest text-neutral-500">Live preview</span>
              <span className="text-xs text-neutral-400">Visitor-facing splash</span>
            </div>
            <div className="bg-[#F9FAFB]" data-testid="cover-live-preview">
              <CoverSplashContent content={content} />
            </div>
          </div>
        </div>
      </div>
    </Layout>
  );
}
